/**
 * Permission system for the backoffice.
 * Mirrors api/src/lib/auth/permissionGroups.ts
 */

export const ROLE_PERMISSIONS: Record<string, string[]> = {
    // Super Admin roles
    SUPER_ADMIN: ['*'],
    ADMIN: [
        'vendors:*', 'products:*', 'bookings:*', 'fees:*', 'providers:*', 'api_keys:*',
        'plans:*',
        'users:list', 'users:invite',
        'platform:analytics',
    ],
    MODERATOR: [
        'vendors:list', 'vendors:view',
        'products:list', 'products:view', 'products:update',
        'bookings:*',
        'platform:analytics',
    ],
    VIEWER: [
        'vendors:list', 'vendors:view',
        'products:list', 'products:view',
        'bookings:list', 'bookings:view',
    ],

    // Vendor roles
    OWNER: [
        'vendor:*', 'vendor_users:*', 'products:*', 'bookings:*',
        'orders:read', 'orders:update', 'payments:read',
        'analytics:read', 'settings:*',
    ],
    VENDOR_ADMIN: [
        'vendor:read', 'vendor:update',
        'vendor_users:read', 'vendor_users:create',
        'products:*', 'bookings:read', 'bookings:update',
        'orders:read', 'orders:update', 'payments:read',
        'analytics:read', 'settings:read', 'settings:update',
    ],
    VENDOR_MODERATOR: [
        'vendor:read',
        'products:read', 'products:update', 'products:create',
        'bookings:read', 'bookings:update',
        'orders:read', 'analytics:read',
    ],
    VENDOR_VIEWER: [
        'vendor:read',
        'products:read', 'bookings:read', 'orders:read',
    ],
};

export const ROLE_LABELS: Record<string, string> = {
    SUPER_ADMIN: 'Super Admin',
    ADMIN: 'Admin',
    MODERATOR: 'Moderator',
    VIEWER: 'Viewer',
    OWNER: 'Owner',
    VENDOR_ADMIN: 'Vendor Admin',
    VENDOR_MODERATOR: 'Vendor Moderator',
    VENDOR_VIEWER: 'Vendor Viewer',
};

export const ROLE_DESCRIPTIONS: Record<string, string> = {
    SUPER_ADMIN: 'Full access to everything. Can manage all admins.',
    ADMIN: 'Manage vendors, products, bookings, fees, API keys, and plans. Cannot manage other admins.',
    MODERATOR: 'View vendors and products. Manage bookings.',
    VIEWER: 'Read-only access to vendors, products, and bookings.',
    OWNER: 'Full access to own vendor. Can manage team.',
    VENDOR_ADMIN: 'Manage products, bookings, settings. Can invite team.',
    VENDOR_MODERATOR: 'Manage products and bookings.',
    VENDOR_VIEWER: 'Read-only access.',
};

export function hasPermission(role: string, permission: string): boolean {
    const perms = ROLE_PERMISSIONS[role];
    if (!perms) return false;
    if (perms.includes('*')) return true;
    if (perms.includes(permission)) return true;
    const [resource] = permission.split(':');
    if (perms.includes(`${resource}:*`)) return true;
    return false;
}

export function hasAnyPermission(role: string, permissions: string[]): boolean {
    return permissions.some((p) => hasPermission(role, p));
}
